/**
 * Maps Work ID data to task modal initial values for prepopulation.
 */
import type { WorkIdDetail } from '../../../services/workIdService';

export interface QuantifiedInitialData {
  activityId?: number;
  activityNumber?: string;
  activityName?: string;
  unitOfMeasure?: string;
  identifiedQuantity?: number;
  foremanAreaId?: number;
  contractAreaId?: number;
  contractYear: string;
  /** Suggested task name from Work ID (e.g. "Activity Name (#123) - Asset Name") */
  name?: string;
  taskLocations: Array<{
    type: 'START' | 'END';
    latitude: number;
    longitude: number;
    assetId?: number | null;
  }>;
}

export interface RoutineInitialData {
  activityIds?: number[];
  foremanAreaId?: number;
  contractAreaId?: number;
  completionAccomplishment?: number;
  taskLocations: Array<{
    type: 'START' | 'END';
    latitude: number;
    longitude: number;
    assetId?: number | null;
  }>;
}

export function getQuantifiedInitialDataFromWorkId(workId: WorkIdDetail | null): QuantifiedInitialData {
  const empty: QuantifiedInitialData = {
    contractYear: '7',
    taskLocations: [],
  };

  if (!workId) return empty;

  const taskLocations: QuantifiedInitialData['taskLocations'] = [];
  const lat = workId.latitude ?? workId.asset?.startLatitude ?? null;
  const lng = workId.longitude ?? workId.asset?.startLongitude ?? null;
  if (lat != null && lng != null) {
    taskLocations.push({
      type: 'START',
      latitude: lat,
      longitude: lng,
      assetId: workId.asset?.id ?? undefined,
    });
  }

  const contractAreaId =
    workId.asset?.contractAreaId ?? undefined;

  const assetName = workId.asset?.name ?? '';
  const commentsSnippet = workId.comments?.trim() ? workId.comments.trim().slice(0, 50) + (workId.comments.length > 50 ? '…' : '') : '';
  const workIdLabel = workId.workIdentificationId ?? `Work ID #${workId.id}`;
  const name = assetName && commentsSnippet
    ? `${commentsSnippet} – ${assetName}`
    : assetName || (workIdLabel ? `${workIdLabel} – Work ID` : undefined);

  return {
    foremanAreaId: workId.foremanAreaId ?? workId.foremanArea?.id,
    contractAreaId,
    contractYear: '7',
    name,
    taskLocations,
  };
}

export function getRoutineInitialDataFromWorkId(workId: WorkIdDetail | null): RoutineInitialData {
  const empty: RoutineInitialData = {
    taskLocations: [],
  };

  if (!workId) return empty;

  const taskLocations: RoutineInitialData['taskLocations'] = [];
  const lat = workId.latitude ?? workId.asset?.startLatitude ?? null;
  const lng = workId.longitude ?? workId.asset?.startLongitude ?? null;
  if (lat != null && lng != null) {
    taskLocations.push({
      type: 'START',
      latitude: lat,
      longitude: lng,
      assetId: workId.asset?.id ?? undefined,
    });
  }

  const contractAreaId =
    workId.asset?.contractAreaId ?? undefined;

  return {
    foremanAreaId: workId.foremanAreaId ?? workId.foremanArea?.id,
    contractAreaId,
    taskLocations,
  };
}
